import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: ReactNode;
  headerTrailing?: ReactNode;
  children: ReactNode;
}

export function SidePanel({
  open,
  onOpenChange,
  title,
  subtitle,
  headerTrailing,
  children,
}: SidePanelProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !e.defaultPrevented) onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <aside
      role="complementary"
      aria-label={title}
      className="flex w-full flex-1 flex-col overflow-hidden rounded-2xl border border-white/6 bg-[#0F1F1C] text-white/90 shadow-[0_12px_32px_rgba(0,0,0,0.4)] sm:w-80 sm:flex-none md:w-90"
    >
      <header className="flex items-start justify-between gap-3 border-b border-white/6 px-5 pt-5 pb-4">
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[20px] leading-tight text-white italic">{title}</h2>
          {subtitle ? <div className="mt-1 text-[12px] text-white/55">{subtitle}</div> : null}
        </div>
        {headerTrailing ? <div className="flex-none">{headerTrailing}</div> : null}
        <button
          type="button"
          aria-label="Close panel"
          onClick={() => {
            onOpenChange(false);
          }}
          className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/8 hover:text-white focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none"
        >
          <X className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
        </button>
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </aside>
  );
}
